<template>
  <result-item-template :name="result.item.name" :icon-glyph="iconGlyph">
    <template #metadata>
      <ys-text
        type="metadata1"
        class="result-details-subtext"
        data-test="search-result-subtext"
      >
        <span v-if="cityState">
          {{ cityState }}
        </span>
        <!-- Distance away from user -->
        <span v-if="distanceInKilometers">
          <span v-if="cityState" class="divider">|</span>
          <x-distance
            from="kilometers"
            label-type="full"
            :length="distanceInKilometers"
          />
          away
        </span>
        <!-- Parent waterbody of result item -->
        <span v-if="waterbodyName" class="divider">|</span>
        <span v-if="waterbodyName" data-test="waterbody-text">
          {{ waterbodyName }}
        </span>
      </ys-text>
    </template>
  </result-item-template>
</template>

<script setup lang="ts">
///// imports /////
import { computed } from 'vue'
import {
  getCityState,
  getDistanceInKilometers,
} from '@/lib/search/search-utils'
import '@onxmaps/yellowstone-ui/ys-text'
import xDistance from '@/components/shared/x-distance.vue'
import resultItemTemplate from '@/components/search/result-items/result-item-template.vue'

import type { SearchResultProps } from '@/services/search-service/types'
import type { WaterAccessSite } from '@onxmaps/supergraph'

///// refs/variables /////
// FUTURE: The slugs returned from KG do not map to the glpyhMap Icons
// Eventually/hopefully, they will
const slugGlyphMapping = {
  dock: 'DOCK',
  toilet: 'INFORMATION',
  'flush-toilet': 'INFORMATION',
  fishing: 'FISHING_ACCESS',
  'bank-fishing': 'SHORE_FISHING',
  'breakwater-fishing': 'FISHING_PIER',
  'pier-fishing': 'FISHING_PIER',
  'shore-fishing': 'SHORE_FISHING',
  parking: 'PARKING_AREA',
  'ada-parking': 'PARKING_AREA',
  'trailer-parking': 'BOAT_TRAILER_PARKING',
  'boat-launch': 'BOAT_RAMP',
  'carry-in-boat-launch': 'HAND_LAUNCH',
  'trailer-boat-launch': 'BOAT_RAMP',
} as const satisfies Record<string, string>

///// props/emits /////
// Defining a new variant of WaterAccessSite that aliases the waterbody as parentWaterbody
interface AliasedWaterAccessSite extends WaterAccessSite {
  waterbody?: never
  parentWaterbody: WaterAccessSite['waterbody']
}

// Special SearchResultProps variant denoted as a WaterAccessSite result
interface WaterAccessSearchResult extends SearchResultProps {
  about: AliasedWaterAccessSite
}
interface WaterAccessItemProps {
  result: WaterAccessSearchResult
}
const { result } = defineProps<WaterAccessItemProps>()

///// computed /////
const waterbodyName = computed(() => result.about?.parentWaterbody?.name)
const iconGlyph = computed(() => {
  const slug = result.about?.primaryAmenity
    ?.slug as keyof typeof slugGlyphMapping
  return slugGlyphMapping[slug] ?? 'INFORMATION'
})

const cityState = computed(() => getCityState(result))

const distanceInKilometers = computed(() => {
  const point = result.item.point
  return point ? getDistanceInKilometers(point) : undefined
})
</script>

<style lang="scss" scoped>
.divider {
  padding: 0 var(--ys-spacing-25);
}
</style>
